import { Column } from "@/components/Datatable/types";
import { CampaignStatusItem } from "../../api/fetchCampaignStatus/types";
import React from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Eye, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

const getStatusColor = (statusName: string): string => {
  const normalizedStatus = statusName.toLowerCase();
  switch (normalizedStatus) {
    case "active":
      return "bg-green-100 text-green-700 border-transparent";
    case "completed":
      return "bg-blue-100 text-blue-700 border-transparent";
    case "pending":
      return "bg-yellow-100 text-yellow-700 border-transparent";
    case "paused":
      return "bg-yellow-100 text-yellow-700 border-transparent";
    case "archived":
      return "bg-gray-300 text-gray-600 border-transparent";
    default:
      return "bg-gray-100 text-gray-700 border-transparent";
  }
};

export function campaignStatusColumns(): Column<CampaignStatusItem>[] {
  return [
    {
      header: "Company",
      accessorKey: "company",
      cell: ({ row }) => row.original.company.name,
    },
    {
      header: "Intacct ID",
      accessorKey: "company",
      cell: ({ row }) => row.original.company.intacctId,
    },
    {
      header: "Campaign Name",
      accessorKey: "name",
      cell: ({ row }) => row.original.name,
    },
    {
      header: "Frequency",
      accessorKey: "mailingFrequencyWeeks",
      cell: ({ row }) => `${row.original.mailingFrequencyWeeks} weeks`,
    },
    {
      header: "Product",
      accessorKey: "campaignProduct.name",
      cell: ({ row }) => row.original.campaignProduct?.name || "—",
    },
    {
      header: "Start Date",
      accessorKey: "startDate",
      cell: ({ row }) => {
        try {
          return row.original.startDate
            ? format(new Date(row.original.startDate), "MM/dd/yyyy")
            : "—";
        } catch {
          return row.original.startDate || "—";
        }
      },
    },
    {
      header: "End Date",
      accessorKey: "endDate",
      cell: ({ row }) => {
        try {
          return row.original.endDate
            ? format(new Date(row.original.endDate), "MM/dd/yyyy")
            : "—";
        } catch {
          return row.original.endDate || "—";
        }
      },
    },
    {
      header: "Last Updated",
      accessorKey: "updatedAt",
      cell: ({ row }) => {
        try {
          return row.original.updatedAt
            ? format(new Date(row.original.updatedAt), "MM/dd/yyyy HH:mm")
            : "—";
        } catch {
          return row.original.updatedAt || "—";
        }
      },
    },
    {
      header: "Status",
      accessorKey: "campaignStatus.name",
      cell: ({ row }) => {
        const statusName = row.original.campaignStatus?.name;
        if (!statusName) return "—";

        return (
          <Badge className={getStatusColor(statusName)}>{statusName}</Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-1">
            <Link
              className="inline-flex p-2 hover:bg-gray-100 rounded-md transition-colors"
              title="View campaign details"
              to={`/stochastic/campaigns/${row.original.id}/view`}
            >
              <Eye className="w-4 h-4 text-gray-600" />
            </Link>
            <Link
              className="inline-flex p-2 hover:bg-gray-100 rounded-md transition-colors"
              title="View analytics"
              to={`/stochastic?intacctId=${row.original.company.intacctId}`}
            >
              <BarChart3 className="w-4 h-4 text-gray-600" />
            </Link>
          </div>
        );
      },
    },
  ];
}
